import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, CreditCard, KeyRound, LogOut, Mail, MapPin, Phone, ShieldCheck, UserRound } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { changeMyPassword, deleteMyAccount } from '../lib/adminApi';
import PortalPageShell from '../components/PortalPageShell';
import Pagination from '../components/Pagination';
import { usePaged } from '../hooks/use-paged';
import { fetchCurrentUser, fetchMyBookings, getCurrentSession, logoutUser } from '../lib/portalApi';
import { getSessionEventName, hasAuthSession } from '../lib/api';
import { useT } from '../i18n/LanguageContext';

const euroFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});

export default function MyAccount() {
  const t = useT();
  const statusLabels = {
    confirmed: t('auth.stConfirmed'),
    payment_pending: t('auth.stPaymentPending'),
    payment_failed: t('auth.stPaymentFailed'),
    pending: t('auth.stPending'),
    cancelled: t('auth.stCancelled'),
  };
  const [session, setSession] = useState(getCurrentSession());
  const [logoutDone, setLogoutDone] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '' });
  const [selfMsg, setSelfMsg] = useState({ ok: '', err: '' });
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const isLoggedIn = hasAuthSession();

  useEffect(() => {
    const syncSession = () => setSession(getCurrentSession());
    const sessionEvent = getSessionEventName();

    window.addEventListener(sessionEvent, syncSession);
    window.addEventListener('storage', syncSession);

    return () => {
      window.removeEventListener(sessionEvent, syncSession);
      window.removeEventListener('storage', syncSession);
    };
  }, []);

  const userQuery = useQuery({
    queryKey: ['account', 'user'],
    queryFn: fetchCurrentUser,
    enabled: isLoggedIn,
    initialData: session.user,
  });

  const bookingsQuery = useQuery({
    queryKey: ['account', 'bookings'],
    queryFn: fetchMyBookings,
    enabled: isLoggedIn,
    initialData: [],
  });

  const user = userQuery.data || session.user;
  const isAdmin = user?.role === 'admin';
  const bookings = bookingsQuery.data || [];
  const bookingsPaged = usePaged(bookings, 10);

  const handleLogout = () => {
    logoutUser();
    setSession(getCurrentSession());
    setLogoutDone(true);
  };

  const passwordMutation = useMutation({
    mutationFn: () => changeMyPassword(pw.current, pw.next),
    onSuccess: () => {
      setSelfMsg({ ok: 'Contraseña actualizada correctamente.', err: '' });
      setPw({ current: '', next: '' });
    },
    onError: (e) => setSelfMsg({ ok: '', err: e.message || 'No se pudo cambiar la contraseña.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteMyAccount(delPassword, 'anonymize'),
    onSuccess: () => {
      // The account no longer exists: drop the session and show the public view.
      logoutUser();
      setSession(getCurrentSession());
      setShowDelete(false);
      setLogoutDone(true);
    },
    onError: (e) => setSelfMsg({ ok: '', err: e.message || 'No se pudieron eliminar los datos.' }),
  });

  // Admins manage everything (including their own profile) from the single admin panel.
  if (isLoggedIn && isAdmin) {
    return <Navigate to="/admin/account" replace />;
  }

  if (!isLoggedIn) {
    return (
      <PortalPageShell
        eyebrow={t('auth.accountEyebrow')}
        title={t('auth.myAccount')}
        description={t('auth.noSessionDesc')}
        widthClassName="max-w-4xl"
      >
        <div className="rounded-[30px] border border-white/70 bg-white/90 p-8 md:p-10 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.28)]">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h2 className="text-2xl font-bold text-[#14213d]">{t('auth.noSessionTitle')}</h2>
              <p className="mt-3 max-w-2xl text-slate-600">
                {t('auth.noSessionDesc')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/login" className="rounded-full bg-[#14213d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f1a31]">
                {t('auth.loginBtn')}
              </Link>
              <Link to="/register" className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                {t('auth.createAccount')}
              </Link>
            </div>
          </div>
        </div>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell
      eyebrow={t('auth.privateEyebrow')}
      title={user?.name ? `${t('auth.hello')} ${user.name}` : t('auth.myAccount')}
      description={t('auth.accountDesc')}
      actions={(
        <>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-full bg-[#14213d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f1a31]"
          >
            <LogOut className="h-4 w-4" />
            {t('auth.logout')}
          </button>
        </>
      )}
    >
      {logoutDone ? (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {t('auth.loggedOut')}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
        <section className="rounded-[28px] border border-white/70 bg-white/90 p-7 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#14213d] text-white">
              <UserRound className="h-6 w-6 text-[#c8a25a]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#14213d]">{t('auth.profile')}</h2>
              <p className="mt-1 text-sm text-slate-500">{t('auth.profileSub')}</p>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {[
              { icon: Mail, label: 'Email', value: user?.email || t('auth.notProvided') },
              { icon: Phone, label: t('auth.phone'), value: user?.phone || t('auth.notProvided') },
              { icon: ShieldCheck, label: t('auth.role'), value: user?.role || 'customer' },
              { icon: MapPin, label: t('auth.company'), value: user?.company || t('auth.directCustomer') },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                <item.icon className="h-5 w-5 text-[#1fa5a3]" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{item.label}</p>
                  <p className="mt-1 text-sm font-medium text-[#14213d]">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[28px] border border-white/70 bg-white/90 p-7 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-[#14213d]">{t('auth.myBookings')}</h2>
              <p className="mt-1 text-sm text-slate-500">{t('auth.bookingsSub')}</p>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {bookingsQuery.isFetching ? t('auth.updating') : `${bookings.length} items`}
            </div>
          </div>

          {bookingsQuery.isError ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {bookingsQuery.error.message || t('auth.bookingsError')}
            </div>
          ) : null}

          {bookings.length === 0 && !bookingsQuery.isLoading ? (
            <div className="mt-8 rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
              <CalendarDays className="mx-auto h-10 w-10 text-[#1fa5a3]" />
              <h3 className="mt-4 text-xl font-semibold text-[#14213d]">{t('auth.noBookingsTitle')}</h3>
              <p className="mt-2 text-sm text-slate-500">
                {t('auth.noBookingsText')}
              </p>
            </div>
          ) : null}

          <div className="mt-6 space-y-4">
            {bookingsPaged.pageItems.map((booking) => (
              <article key={booking.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1fa5a3]">
                      {statusLabels[booking.status] || booking.status}
                    </p>
                    <h3 className="mt-2 text-lg font-bold text-[#14213d]">{booking.title}</h3>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-[#c8a25a]" />
                        {booking.date || t('auth.datePending')}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-[#c8a25a]" />
                        {booking.amount > 0 ? euroFormatter.format(booking.amount) : t('auth.amountPending')}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('auth.reference')}</p>
                    <p className="mt-1 text-sm font-medium text-[#14213d]">{booking.id}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <Pagination page={bookingsPaged.page} pageCount={bookingsPaged.pageCount} onPageChange={bookingsPaged.setPage} pageSize={bookingsPaged.pageSize} onPageSizeChange={bookingsPaged.setPageSize} pageSizes={[10, 25, 50]} />
        </section>
      </div>

      {/* Security + privacy (self-service) */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-[28px] border border-white/70 bg-white/90 p-7 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
              <KeyRound className="h-5 w-5 text-[#c8a25a]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#14213d]">Cambiar contraseña</h2>
              <p className="text-sm text-slate-500">Necesitas tu contraseña actual.</p>
            </div>
          </div>

          {selfMsg.ok ? <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{selfMsg.ok}</div> : null}
          {selfMsg.err ? <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{selfMsg.err}</div> : null}

          <div className="mt-5 space-y-4">
            <input
              type="password" autoComplete="current-password" placeholder="Contraseña actual"
              value={pw.current} onChange={(e) => setPw((c) => ({ ...c, current: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10"
            />
            <input
              type="password" autoComplete="new-password" placeholder="Nueva contraseña (8+, letras y números)"
              value={pw.next} onChange={(e) => setPw((c) => ({ ...c, next: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10"
            />
            <button
              type="button"
              disabled={!pw.current || pw.next.length < 8 || passwordMutation.isPending}
              onClick={() => { setSelfMsg({ ok: '', err: '' }); passwordMutation.mutate(); }}
              className="w-full rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
            >
              {passwordMutation.isPending ? 'Guardando…' : 'Actualizar contraseña'}
            </button>
          </div>
        </section>

        <section className="rounded-[28px] border border-red-200 bg-white/90 p-7 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#14213d]">Eliminar mis datos</h2>
              <p className="text-sm text-slate-500">Derecho de supresión (RGPD).</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Se borrará tu cuenta y tus datos personales de forma permanente. Por obligación legal se conserva
            el registro contable de tus reservas, pero sin ningún dato que te identifique.
          </p>

          {!showDelete ? (
            <button
              type="button"
              onClick={() => { setShowDelete(true); setSelfMsg({ ok: '', err: '' }); }}
              className="mt-5 rounded-full border border-red-300 px-5 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50"
            >
              Solicitar borrado de mis datos
            </button>
          ) : (
            <div className="mt-5 space-y-4">
              <input
                type="password" autoComplete="current-password" placeholder="Confirma con tu contraseña"
                value={delPassword} onChange={(e) => setDelPassword(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-100"
              />
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowDelete(false); setDelPassword(''); }}
                  className="flex-1 rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={!delPassword || deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                  className="flex-1 rounded-full bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Eliminando…' : 'Eliminar definitivamente'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </PortalPageShell>
  );
}
